// third-party Chatwoot Client API
//
// Chatwoot's public Client API is a third-party service that is NOT part of our
// OpenAPI schema, so — like the RxNorm/CDC/Google integrations elsewhere in the
// app — we talk to it with raw `fetch` and tight hand-written response types
// rather than the generated openapi-fetch client, and with no unsafe casts.
//
// Endpoint shape (API channel inbox):
//   {baseUrl}/public/api/v1/inboxes/{inbox_identifier}/...
// where `inbox_identifier` is the Client-API (`Channel::Api`) inbox's identifier
// (EXPO_PUBLIC_CHATWOOT_API_INBOX_IDENTIFIER). Chatwoot's public Client API only
// resolves `Channel::Api` inboxes, so the website widget token does NOT work
// here — this must be a dedicated API-channel inbox whose identity-validation
// HMAC secret is the one the backend signs with.
//
// Identity validation: when Chatwoot has HMAC identity validation enabled for
// the inbox, POSTing a contact with `identifier` + `identifier_hash` upserts and
// links to the SAME stable contact for that identifier across sessions. The
// hash is minted server-side by `GET /me/support/identity`.

import { Platform } from "react-native";
import { ApiRequestError } from "@src/packages/api/api-error";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

const CHATWOOT_BASE_URL =
  process.env.EXPO_PUBLIC_CHATWOOT_BASE_URL ?? "https://app.chatwoot.com";
const INBOX_IDENTIFIER =
  process.env.EXPO_PUBLIC_CHATWOOT_API_INBOX_IDENTIFIER ?? "";

// TODO: Replace these support-conversation types with API-generated types once
// the paired dashboard API exposes them in the OpenAPI schema. They mirror the
// modal's needs and are independent of Chatwoot's own wire shapes below.
export type SupportAttachment = {
  id: string;
  name: string | null;
  contentType: string | null;
  url: string | null;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
};

export type SupportMessage = {
  id: string;
  content: string;
  direction: "patient" | "team";
  createdAt: string;
  attachments: SupportAttachment[];
};

export type SupportConversation = {
  conversationId: number | null;
  messages: SupportMessage[];
};

export type SupportAttachmentDraft = {
  uri: string;
  name: string;
  mimeType?: string | null;
  size?: number | null;
  /** Expo DocumentPicker supplies a browser File on web. */
  file?: unknown;
};

// --- Chatwoot wire shapes (third-party Chatwoot Client API) -----------------

type ChatwootContact = {
  source_id: string;
  pubsub_token?: string | null;
};

type ChatwootConversation = {
  id: number;
  status?: string;
};

type ChatwootAttachment = {
  id: number;
  file_type?: string | null;
  data_url?: string | null;
  thumb_url?: string | null;
  width?: number | null;
  height?: number | null;
};

type ChatwootMessage = {
  id: number;
  content?: string | null;
  // 0 = incoming (from the contact/patient), 1 = outgoing (from the team).
  // 2/3 are activity/template system rows we filter out.
  message_type: number;
  created_at?: number;
  attachments?: ChatwootAttachment[] | null;
};

/** The file descriptor shape accepted by React Native's FormData bridge. */
type NativeFormDataFile = {
  uri: string;
  name: string;
  type: string;
};

/**
 * DOM FormData types only know about Blob values, while React Native also
 * supports a URI-backed file descriptor. Keep that platform capability typed
 * rather than erasing it with an unsafe cast at the call site.
 */
type SupportFormData = FormData & {
  append(
    name: string,
    value: string | Blob | NativeFormDataFile,
    fileName?: string,
  ): void;
};

function inboxPath(suffix: string) {
  return `${CHATWOOT_BASE_URL}/public/api/v1/inboxes/${INBOX_IDENTIFIER}${suffix}`;
}

function messageFromResponse(payload: unknown, fallback: string) {
  if (
    payload &&
    typeof payload === "object" &&
    "message" in payload &&
    typeof payload.message === "string" &&
    payload.message
  ) {
    return payload.message;
  }
  return fallback;
}

async function parseError(response: Response, fallback: string) {
  try {
    return messageFromResponse(await response.json(), fallback);
  } catch {
    return fallback;
  }
}

/**
 * Runs a Client API request and parses the JSON body into the caller's tight
 * hand-written type. Response.json() is typed loosely by the platform lib, so
 * the annotated assignment narrows it without a cast.
 */
async function chatwootJson<T>(
  url: string,
  init: RequestInit | undefined,
  fallback: string,
  context: string,
): Promise<T> {
  if (!INBOX_IDENTIFIER) {
    throw new ApiRequestError("Chat support is not configured.", { context });
  }
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    throw new ApiRequestError(fallback, { cause, context });
  }
  if (!response.ok) {
    throw new ApiRequestError(await parseError(response, fallback), {
      status: response.status,
      context,
    });
  }
  const data: T = await response.json();
  return data;
}

function attachmentName(dataUrl: string | null | undefined): string | null {
  if (!dataUrl) return null;
  const withoutQuery = dataUrl.split("?")[0] ?? dataUrl;
  const base = withoutQuery.slice(withoutQuery.lastIndexOf("/") + 1);
  if (!base) return null;
  try {
    return decodeURIComponent(base);
  } catch {
    return base;
  }
}

function mapAttachment(attachment: ChatwootAttachment): SupportAttachment {
  return {
    id: String(attachment.id),
    name: attachmentName(attachment.data_url) ?? "Attachment",
    // Map Chatwoot's coarse file_type into the MIME-like shape the modal uses
    // to select the inline image renderer.
    contentType:
      attachment.file_type === "image"
        ? "image/*"
        : (attachment.file_type ?? null),
    url: attachment.data_url ?? null,
    thumbnailUrl: attachment.thumb_url ?? null,
    width:
      typeof attachment.width === "number" && attachment.width > 0
        ? attachment.width
        : null,
    height:
      typeof attachment.height === "number" && attachment.height > 0
        ? attachment.height
        : null,
  };
}

export function mapChatwootMessage(message: ChatwootMessage): SupportMessage {
  return {
    id: String(message.id),
    content: message.content ?? "",
    direction: message.message_type === 1 ? "team" : "patient",
    createdAt: new Date((message.created_at ?? 0) * 1000).toISOString(),
    attachments: (message.attachments ?? []).map(mapAttachment),
  };
}

// --- Public operations ------------------------------------------------------

export type ChatwootContactSession = {
  contactIdentifier: string;
  pubsubToken: string | null;
};

/**
 * Create/identify the contact for the signed-in patient. With identity
 * validation ON, Chatwoot upserts by `identifier`, so this returns the same
 * stable contact (source_id) every session.
 */
export async function identifyChatwootContact(
  identifier: string,
  identifierHash: string,
): Promise<ChatwootContactSession> {
  const contact = await chatwootJson<ChatwootContact>(
    inboxPath("/contacts"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        identifier,
        identifier_hash: identifierHash,
        // Key the contact-inbox to the app user id. Chatwoot looks contact-
        // inboxes up by `source_id` (public create: `source_id = params[:source_id]
        // || SecureRandom.uuid`; builder reuses on `find_by(source_id:)`), so
        // reusing the signed-in user's id here resolves to the SAME contact-inbox
        // — and thus the same conversation — every session and device, with no
        // server-side persistence. The user is always authenticated, so the id
        // is always available.
        source_id: identifier,
      }),
    },
    "We couldn't start your support chat.",
    "chatwoot.identifyContact",
  );
  return {
    contactIdentifier: contact.source_id,
    pubsubToken: contact.pubsub_token ?? null,
  };
}

/** Find the contact's reusable conversation without creating an empty one. */
export async function findChatwootConversation(
  contactIdentifier: string,
): Promise<number | null> {
  const existing = await chatwootJson<ChatwootConversation[]>(
    inboxPath(`/contacts/${contactIdentifier}/conversations`),
    undefined,
    "We couldn't load your messages.",
    "chatwoot.listConversations",
  );
  // Prefer an open thread; otherwise fall back to the newest one so replies
  // land in a single continuous conversation instead of fanning out.
  const reusable =
    existing.find((conversation) => conversation.status !== "resolved") ??
    existing[existing.length - 1];
  return reusable?.id ?? null;
}

/** Create a conversation immediately before sending its first message. */
export async function createChatwootConversation(
  contactIdentifier: string,
): Promise<number> {
  const created = await chatwootJson<ChatwootConversation>(
    inboxPath(`/contacts/${contactIdentifier}/conversations`),
    { method: "POST" },
    "We couldn't start your support chat.",
    "chatwoot.createConversation",
  );
  return created.id;
}

export async function listChatwootMessages(
  contactIdentifier: string,
  conversationId: number,
): Promise<SupportMessage[]> {
  const messages = await chatwootJson<ChatwootMessage[]>(
    inboxPath(
      `/contacts/${contactIdentifier}/conversations/${conversationId}/messages`,
    ),
    undefined,
    "We couldn't load your messages.",
    "chatwoot.listMessages",
  );
  return messages
    .filter(
      (message) => message.message_type === 0 || message.message_type === 1,
    )
    .map(mapChatwootMessage)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function sendChatwootMessage(
  contactIdentifier: string,
  conversationId: number,
  content: string,
  attachments: SupportAttachmentDraft[],
  entryPoint: "support" | "feedback" = "support",
): Promise<SupportMessage> {
  const url = inboxPath(
    `/contacts/${contactIdentifier}/conversations/${conversationId}/messages`,
  );

  // Support and feedback share one conversation, and Chatwoot's public message
  // API only permits `content` + `echo_id` (no content_attributes/metadata), so
  // the only reliable per-message signal for the team is the content itself.
  // Mark feedback so it stays distinguishable in the shared thread.
  const outgoing =
    entryPoint === "feedback" && content ? `[Feedback] ${content}` : content;

  let init: RequestInit;
  if (attachments.length === 0) {
    init = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: outgoing }),
    };
  } else {
    const formData = new FormData() as SupportFormData;
    if (outgoing) formData.append("content", outgoing);
    for (const attachment of attachments) {
      // On web Expo exposes a File. Native expects React Native's
      // { uri, name, type } shape. Do not set Content-Type manually: fetch
      // supplies the multipart boundary for both platforms.
      if (
        Platform.OS === "web" &&
        typeof Blob !== "undefined" &&
        attachment.file instanceof Blob
      ) {
        formData.append("attachments[]", attachment.file, attachment.name);
      } else {
        formData.append("attachments[]", {
          uri: attachment.uri,
          name: attachment.name,
          type: resolveUploadContentType(attachment),
        });
      }
    }
    init = { method: "POST", body: formData };
  }

  const created = await chatwootJson<ChatwootMessage>(
    url,
    init,
    "We couldn't send that message. Try again.",
    "chatwoot.sendMessage",
  );
  return mapChatwootMessage(created);
}
